import { useMemo, useState } from 'react'
import { buildSkeleton } from '@/domain/hs1'
import { mjcfModel, rigFromModel } from '@/domain/rig'
import { Alert, Badge, Button, Card, Table, Td, Th } from '@/components/ui'
import { CycleChart } from '@/sim/CycleChart'
import { Metric, PageHead, num, pct } from '@/sim/parts'
import { useSimulation } from '@/sim/SimulationProvider'
import { DEFAULT_GAINS, trackPlan } from '@/sim/mujoco/track'
import type { RunResult } from '@/sim/mujoco/engine'

/**
 * Ein Ergebnis MIT dem Plan, zu dem es gehört.
 *
 * Sonst stünde nach einer verschobenen Gelenkachse ein Ergebnis auf der Seite,
 * das zu einem anderen Roboter gehört — und nichts sagte es.
 */
type Lauf = RunResult & { plan: ReturnType<typeof trackPlan> }

/**
 * Der Prüfstand — dieselbe Maschine, vorwärts gerechnet.
 *
 * WAS DIESE SEITE BEANTWORTET, und was keine andere beantwortet. Alle übrigen
 * Seiten zeigen dieselbe Rechnung aus verschiedenen Blickwinkeln: eine Bewegung
 * ist vorgegeben, die Kräfte folgen daraus. Hier läuft es andersherum — die
 * Momente sind vorgegeben, die Bewegung stellt sich ein. Wo beide Wege
 * übereinstimmen, ist das eine Aussage über das Modell. Wo sie auseinandergehen,
 * steht an genau dieser Stelle eine Frage.
 *
 * DAS WEBASSEMBLY WIEGT 8,6 MB und wird deshalb erst auf Klick geholt. Wer die
 * Seite nur öffnet, zahlt nichts dafür.
 *
 * WAS DIE SEITE NICHT IST: eine Abnahme. Der Nachführregler fährt Gelenkwinkel
 * nach und hält kein Gleichgewicht — ohne einen solchen Regler fällt der
 * Roboter, und das ist eine Aussage über den fehlenden Regler und keine über
 * die Maschine. Der Gleichgewichtsregler entsteht in der Trainingsstrecke,
 * Station 5 der Werkzeugkette.
 */

export function MujocoPage() {
  const { model, state, simulation, robot } = useSimulation()

  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const [lauf, setLauf] = useState<Lauf | null>(null)

  const { mjcf, plan } = useMemo(() => {
    const skeleton = buildSkeleton(model)
    const rig = rigFromModel(model, state.off)
    const off = new Set(Object.keys(state.off))
    const erzeugt = mjcfModel(model, skeleton, rig, { name: robot.id, off })
    return { mjcf: erzeugt, plan: trackPlan(simulation, rig, erzeugt.joints) }
  }, [model, state, simulation, robot.id])

  /* Ein neuer Modellstand macht das Ergebnis ungültig — es gehörte zum alten. */
  const veraltet = lauf !== null && lauf.plan !== plan

  const starten = async () => {
    setLaeuft(true)
    setFehler(null)
    try {
      /* Erst hier wird geladen: der Zweig mit MuJoCo hängt an diesem Import. */
      const { runForward } = await import('@/sim/mujoco/engine')
      const ergebnis = await runForward(mjcf.xml, plan, { cycles: 4 })
      setLauf({ ...ergebnis, plan })
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e))
    } finally {
      setLaeuft(false)
    }
  }

  const gewicht = simulation.metrics.mass * 9.81
  const achsen = lauf ? achsenZeilen(lauf, plan) : []

  return (
    <div className="stack">
      <PageHead title="Prüfstand" />

      <div className="metrics">
        <Metric
          label="Modell"
          value={`${num(mjcf.mass, 2)} kg`}
          note={`${mjcf.bodies.length} Körper · ${mjcf.joints.length} Achsen`}
        />
        <Metric
          label="Antriebe"
          value={String(mjcf.joints.filter((j) => !j.spring).length)}
          note={`${mjcf.joints.filter((j) => j.spring).length} passive Federn`}
        />
        <Metric
          label="Sollbahn"
          value={`${plan.samples} Stützstellen`}
          note={`${num(plan.cycleTime, 2)} s je Doppelschritt`}
        />
        <Metric
          label="Stand"
          value={lauf ? `${num(lauf.standTime, 2)} s` : '—'}
          note={lauf ? (lauf.fell ? 'dann umgefallen' : 'durchgelaufen') : 'noch nicht gerechnet'}
          warn={lauf?.fell}
        />
      </div>

      <Card
        title="Vorwärtsrechnung starten"
        actions={
          <Button variant="primary" onClick={() => void starten()} disabled={laeuft}>
            {laeuft ? 'rechnet …' : lauf ? 'Neu rechnen' : 'Starten'}
          </Button>
        }
      >
        <p className="page-lead">
          MuJoCo bekommt genau die Datei, die auch der Export liefert, und fährt die gerechneten
          Gelenkwinkel mit den Momentgrenzen der Auslegung nach. Vier Doppelschritte, 1 ms
          Schrittweite.
        </p>
        <p className="metric__note">
          Beim ersten Start werden 8,6 MB WebAssembly geladen. Danach liegen sie im Zwischenspeicher
          des Browsers.
        </p>
        {fehler && (
          <Alert tone="danger" title="Der Lauf ist gescheitert">
            {fehler}
          </Alert>
        )}
        {veraltet && (
          <Alert tone="warning" title="Der Modellstand hat sich geändert">
            Das Ergebnis unten gehört zum vorigen Stand. Neu rechnen.
          </Alert>
        )}
        {plan.unknown > 0 && (
          <Alert tone="info" title={`${plan.unknown} Achsen ohne aufgezeichnete Kurve`}>
            Hüftgieren, Hüftrollen und der Hals haben im Referenzgang keine eigene Kurve — dort
            führt das Gangmodell die Hüfte als ein Gelenk mit drei Ebenen und nicht als drei
            Gelenke. Diese Achsen werden auf null gehalten. Das ist eine Vorgabe und keine
            Bewegung; ein Folgefehler dort sagt nichts über die Auslegung.
          </Alert>
        )}
      </Card>

      {lauf && (
        <>
          {lauf.fell && (
            <Alert
              tone="info"
              title={`Nach ${num(lauf.standTime, 2)} s umgefallen — und das ist die richtige Aussage`}
            >
              Der Nachführregler fährt Gelenkwinkel nach; das Gleichgewicht hält er nicht. Ohne
              einen Gleichgewichtsregler fällt jedes Modell, und ein Modell, das von selbst stehen
              bliebe, hätte irgendwo eine Fessel, die im Roboter nicht ist. Aufgezeichnet ist der
              Zyklus davor.
            </Alert>
          )}

          <Card title="Bodenreaktionskraft — zwei Richtungen, eine Maschine">
            <CycleChart
              title="Senkrechte Bodenkraft"
              unit="N"
              height={240}
              stance={simulation.metrics.stance}
              series={[
                {
                  key: 'kern',
                  label: 'Rechenkern (rückwärts)',
                  values: plan.reference.grf,
                  tone: 1,
                },
                {
                  key: 'mujoco',
                  label: 'MuJoCo (vorwärts)',
                  values: lauf.grf,
                  tone: 3,
                  dashed: true,
                },
              ]}
              limit={{ lo: 0, hi: gewicht * 3, label: 'dreifaches Körpergewicht' }}
            />
            <p className="metric__note">
              Der Kern rechnet die Kraft aus der vorgegebenen Bewegung, MuJoCo aus der
              Schwerpunktbeschleunigung, die sich einstellt. Im Mittel müssen beide das Gewicht
              tragen — {num(gewicht, 0)} N. Wo die Spitzen auseinandergehen, liegt es an der
              Kontaktsteifigkeit: sie ist angenommen und nicht gemessen.
            </p>
          </Card>

          <Card title="Höhe des Schwerpunkts">
            <CycleChart
              title="Schwerpunkt über dem Boden"
              unit="m"
              height={200}
              stance={simulation.metrics.stance}
              series={[
                {
                  key: 'kern',
                  label: 'Rechenkern',
                  values: plan.reference.comHeight,
                  tone: 1,
                },
                { key: 'mujoco', label: 'MuJoCo', values: lauf.comHeight, tone: 3, dashed: true },
              ]}
            />
            <p className="metric__note">
              Die Höhe ist der strengste der drei Kanäle: sie hängt an jeder Segmentmasse und an
              jeder Gliedlänge. Liegen die beiden Kurven übereinander, beschreiben beide Wege
              denselben Roboter.
            </p>
          </Card>

          <Card flush title="Was die Achsen dabei leisten mussten">
            <Table>
              <thead>
                <tr>
                  <Th>Achse</Th>
                  <Th align="right">Moment</Th>
                  <Th align="right">Folgefehler</Th>
                  <Th align="right">An der Grenze</Th>
                  <Th>Befund</Th>
                </tr>
              </thead>
              <tbody>
                {achsen.map((a) => (
                  <tr key={a.name}>
                    <Td>{a.name}</Td>
                    <Td align="right" numeric>
                      {num(a.moment, 0)} Nm
                    </Td>
                    <Td align="right" numeric>
                      {num(a.error, 1)}°
                    </Td>
                    <Td align="right" numeric>
                      {pct(a.saturated, 0)}
                    </Td>
                    <Td>
                      {!a.known ? (
                        <span className="cell-muted">keine Kurve</span>
                      ) : a.saturated > 0.25 ? (
                        <Badge tone="danger">Moment reicht nicht</Badge>
                      ) : a.saturated > 0.05 ? (
                        <Badge tone="warning">stösst an</Badge>
                      ) : (
                        <Badge tone="success">folgt</Badge>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>

          <Card title="Wie das zu lesen ist">
            <p className="page-lead">
              <b>An der Grenze</b> ist der Anteil des Zyklus, in dem der Regler das volle
              Achsmoment gefordert hat. Solange es reicht, folgt die Achse ihrer Kurve; sobald es
              nicht reicht, bleibt sie zurück — und zwar genau dort im Zyklus, wo die Auslegung zu
              schwach ist. Das ist die Auskunft, für die dieser Prüfstand gebaut wurde.
            </p>
            <p className="metric__note">
              Ein <b>Folgefehler ohne</b> Zeit an der Grenze bedeutet etwas anderes: dort ist nicht
              das Moment zu klein, sondern der Regler zu weich. Seine Steifigkeit ist eine Annahme
              dieser Seite und keine Konstruktionsgrösse — volles Moment bei{' '}
              {num((DEFAULT_GAINS.fullAt * 180) / Math.PI, 1)}° Fehler, Vorhaltezeit{' '}
              {num(DEFAULT_GAINS.damping * 1000, 0)} ms. Wer sie ändert, ändert die Folgefehler und
              nicht die Maschine.
            </p>
          </Card>
        </>
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------
   Die Tabelle
   ------------------------------------------------------------------------- */

interface AchsZeile {
  name: string
  moment: number
  /** Grösster Folgefehler, GRAD. */
  error: number
  saturated: number
  known: boolean
}

/**
 * Die Achsen, sortiert nach dem, was sie sagen.
 *
 * NACH DER ZEIT AN DER GRENZE UND NICHT NACH DEM FOLGEFEHLER. Der Fehler ist
 * die Folge, die Grenze der Grund — und eine Liste, die nach der Folge sortiert,
 * setzt eine weich geregelte Achse über eine, deren Antrieb zu klein ist.
 */
function achsenZeilen(lauf: RunResult, plan: ReturnType<typeof trackPlan>): AchsZeile[] {
  const bekannt = new Map(plan.joints.map((j) => [j.joint.name, j]))
  const out: AchsZeile[] = []
  for (const [name, fehler] of lauf.errorByJoint) {
    const tracked = bekannt.get(name)
    if (!tracked) continue
    out.push({
      name,
      moment: tracked.joint.moment,
      error: (fehler * 180) / Math.PI,
      saturated: lauf.saturatedByJoint.get(name) ?? 0,
      known: tracked.known,
    })
  }
  return out.sort((a, b) => b.saturated - a.saturated || b.error - a.error)
}
